import { api, RouterInput, RouterOutput } from "../../../services/rpc/RpcProvider";
import { useCallback } from "react";
import { useActions, useToast } from "@introist/react-foundation/v2";

export type WorkflowFolder = RouterOutput["workflows"]["v4"]["folders"]["find"];
export type WorkflowFolderCreate = RouterInput["workflows"]["v4"]["folders"]["create"];
export type WorkflowFolderUpdate = RouterInput["workflows"]["v4"]["folders"]["update"];

export const useWorkflowFolders = (parentId: string | null) => {
  const { onConfirmAction } = useActions();
  const toast = useToast();

  const { data: folders, refetch } = api.workflows.v4.folders.list.useQuery(
    { parentId },
    {
      refetchOnMount: "always"
    }
  );

  const createOp = api.workflows.v4.folders.create.useMutation();
  const updateOp = api.workflows.v4.folders.update.useMutation();
  const deleteOp = api.workflows.v4.folders.delete.useMutation();

  const create = useCallback(
    async (entry: Partial<WorkflowFolder>) => {
      return createOp.mutateAsync(entry as WorkflowFolderCreate).then(async res => {
        await refetch();
        return res;
      });
    },
    [createOp, refetch]
  );

  const update = useCallback(
    async (entry: WorkflowFolder) => {
      const { id, name } = entry;
      const updates = { name };

      await updateOp.mutateAsync({ id, updates }).then(async () => {
        await refetch();
      });
    },
    [updateOp, refetch]
  );

  const remove = onConfirmAction(
    async id => {
      await deleteOp.mutateAsync({ id });
      await refetch();
      toast.success("Folder deleted");
    },
    {
      icon: "trash",
      title: "Delete folder",
      description: "Are you sure you want to delete this folder?",
      confirmTitle: "Delete",
      cancelTitle: "Cancel"
    }
  );

  return {
    folders,
    create,
    update,
    remove
  };
};
